import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../../app/store/slices/uiSlice";

const LIMIT = 100;

export default function InfiniteSearchSelect({
  label,
  name,
  value,
  onChange,
  fetchFn,
  getOptions,
  getTotal,
  extraParams = {},
  required = false,
  disabled = false,
  error = "",
  placeholder = "Select an option",
}) {
  const darkMode = useSelector(selectDarkMode);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const observerRef = useRef(null);

  const selectedLabel = options.find((o) => o.value === value)?.label || (value ? value : "");

  const fetchPage = useCallback(
    async (pageNum, searchTerm, append = false) => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await fetchFn({ page: pageNum, limit: LIMIT, search: searchTerm, sortBy: "name", order: "asc", ...extraParams });
        const fetched = getOptions(res);
        setOptions((prev) => (append ? [...prev, ...fetched] : fetched));
        const total = getTotal ? getTotal(res) : null;
        setHasMore(total !== null ? pageNum * LIMIT < total : fetched.length === LIMIT);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    [fetchFn, getOptions, extraParams]
  );

  // Fetch on mount to resolve label for pre-selected value (edit mode)
  useEffect(() => {
    if (value) fetchPage(1, "", false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setPage(1);
      setHasMore(true);
      fetchPage(1, "", false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchPage(1, search, false);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver for infinite scroll sentinel
  useEffect(() => {
    if (!isOpen) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchPage(nextPage, search, true);
        }
      },
      { threshold: 1 }
    );
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [isOpen, hasMore, loading, page, search, fetchPage]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (opt) => {
    onChange({ target: { name, value: opt.value } });
    setIsOpen(false);
  };

  const triggerBorder = error
    ? "border-red-500"
    : darkMode
    ? "border-gray-600"
    : "border-gray-300";

  const triggerBg = darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900";
  const dropdownBg = darkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200";
  const itemHover = darkMode ? "hover:bg-gray-700" : "hover:bg-blue-50";

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={`w-full border rounded-lg px-4 py-2.5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${triggerBorder} ${triggerBg} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className={selectedLabel ? "" : "text-gray-400"}>{selectedLabel || placeholder}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 w-auto min-w-[220px] border rounded-lg shadow-lg overflow-hidden ${dropdownBg}`} style={{ maxWidth: containerRef.current?.offsetWidth }}>
          {/* Search input */}
          <div className={`p-2 border-b ${darkMode ? "border-gray-600" : "border-gray-100"}`}>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
            />
          </div>

          {/* Options list */}
          <ul className="overflow-y-auto max-h-52">
            {options.length === 0 && !loading && (
              <li className={`px-4 py-3 text-sm text-center ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No results found</li>
            )}
            {options.map((opt) => (
              <li
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${itemHover} ${opt.value === value ? (darkMode ? "bg-blue-900 text-blue-200" : "bg-blue-50 text-blue-700 font-medium") : darkMode ? "text-gray-200" : "text-gray-800"}`}
              >
                {opt.label}
              </li>
            ))}
            {/* Infinite scroll sentinel */}
            <li ref={sentinelRef} className="h-1" />
            {loading && (
              <li className={`px-4 py-2 text-xs text-center ${darkMode ? "text-gray-400" : "text-gray-400"}`}>Loading...</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
